import type { Doc, Note } from '../types'

const DAY = 86_400_000

/** Apple Notes list dates: time today, weekday this week, otherwise M/D/YY. */
export function listDate(ms: number): string {
  const d = new Date(ms)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

  if (ms >= startOfToday) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }
  if (ms >= startOfToday - DAY) return 'Yesterday'
  if (ms >= startOfToday - 6 * DAY) {
    return d.toLocaleDateString(undefined, { weekday: 'long' })
  }
  return d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: '2-digit' })
}

/** The long form shown at the top of the editor. */
export function editorDate(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

export function daysLeftInTrash(trashedAt: number, days: number): number {
  return Math.max(0, Math.ceil((trashedAt + days * DAY - Date.now()) / DAY))
}

/** Block nodes that render as their own line. */
const TEXT_BLOCKS = new Set(['paragraph', 'heading', 'codeBlock', 'blockquote', 'fileAttachment'])

/** Flatten a ProseMirror doc to plain lines, skipping empty ones. */
function textLines(doc: Doc): string[] {
  const lines: string[] = []
  const visit = (node: any): void => {
    if (!node || typeof node !== 'object') return
    if (TEXT_BLOCKS.has(node.type)) {
      const text = collectText(node).trim()
      if (text) lines.push(text)
      return
    }
    // Containers (doc, bulletList, orderedList, listItem, taskList, taskItem…)
    if (Array.isArray(node.content)) node.content.forEach(visit)
  }
  ;(doc.content ?? []).forEach(visit)
  return lines
}

function collectText(node: any): string {
  if (!node) return ''
  if (typeof node.text === 'string') return node.text
  // A computed answer is an atom with no child text, but it must still show up
  // in previews and full-text search.
  if (node.type === 'mathResult') return String(node.attrs?.value ?? '')
  // Attachment names should be searchable.
  if (node.type === 'fileAttachment') return String(node.attrs?.name ?? '')
  if (!Array.isArray(node.content)) return ''
  return node.content.map(collectText).join('')
}

/** Inline #tags, the way Apple Notes collects them. */
const TAG_RE = /(?:^|\s)#([\p{L}\p{N}_-]{1,60})/gu

export function extractTags(text: string): string[] {
  const out = new Set<string>()
  for (const m of text.matchAll(TAG_RE)) out.add(m[1].toLowerCase())
  return [...out].slice(0, 40)
}

/** Guard against pushing a note past Firestore's 1 MB document ceiling. */
const MAX_TEXT = 100_000

/**
 * Apple Notes derives the list title from the first line and the preview from
 * the rest. An empty note reads "New Note" / "No additional text".
 *
 * `text` is the whole note flattened — it is what full-text search reads, since
 * `snippet` is truncated for display and would silently hide later paragraphs.
 */
export function deriveMeta(doc: Doc): {
  title: string; snippet: string; text: string; tags: string[]
} {
  const lines = textLines(doc)
  const text = lines.join('\n').slice(0, MAX_TEXT)
  return {
    title: (lines[0] ?? '').slice(0, 120) || 'New Note',
    snippet: (lines.slice(1).join('  ') || '').slice(0, 200),
    text,
    tags: extractTags(text),
  }
}

/**
 * Notes written before `text` existed fall back to walking their document.
 * Memoised on id+updatedAt so typing in the search box doesn't re-walk
 * every note on every keystroke.
 */
const textCache = new Map<string, string>()

export function searchableText(note: Note): string {
  if (note.text != null) return note.text
  const key = `${note.id}:${note.updatedAt}`
  const hit = textCache.get(key)
  if (hit != null) return hit
  const derived = textLines(note.content).join('\n')
  if (textCache.size > 500) textCache.clear()
  textCache.set(key, derived)
  return derived
}

export function matchesQuery(note: Note, q: string): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  return (
    note.title.toLowerCase().includes(needle) ||
    searchableText(note).toLowerCase().includes(needle)
  )
}

/**
 * A new note starts in Title style. Apple Notes styles the first line as the
 * title automatically, and pressing Return drops to Body — which is what
 * TipTap does after a heading by default.
 */
export const EMPTY_DOC: Doc = { type: 'doc', content: [{ type: 'heading', attrs: { level: 1 } }] }

/**
 * Apple Notes' list section headers: Today, Yesterday, Previous 7 Days,
 * Previous 30 Days, then month names within this year, then bare years.
 */
export function dateGroupLabel(ms: number): string {
  const d = new Date(ms)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

  if (ms >= startOfToday) return 'Today'
  if (ms >= startOfToday - DAY) return 'Yesterday'
  if (ms >= startOfToday - 7 * DAY) return 'Previous 7 Days'
  if (ms >= startOfToday - 30 * DAY) return 'Previous 30 Days'
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(undefined, { month: 'long' })
  return String(d.getFullYear())
}

/** First image in a note, for the gallery view's thumbnail. */
export function firstImage(doc: Doc): string | null {
  let found: string | null = null
  const visit = (node: any): void => {
    if (found || !node || typeof node !== 'object') return
    if (node.type === 'image' && typeof node.attrs?.src === 'string') { found = node.attrs.src; return }
    if (Array.isArray(node.content)) node.content.forEach(visit)
  }
  ;(doc.content ?? []).forEach(visit)
  return found
}
